import {
  type CatalogEntry,
  type InspectionTemplateResponse,
  catalogListResponseSchema,
  inspectionTemplateResponseSchema,
} from '@sivan/shared';
import { askConfirm } from '../ui/confirm';
import { useEffect, useId, useRef, useState } from 'react';
import { ApiError, NetworkError, apiGet, apiPost, apiPut } from '../api/client';
import { explainProjectError } from './project-setup';
import {
  type TemplateRow,
  addRow,
  isDirty,
  moveRow,
  rowsFrom,
  toRequest,
  validateRows,
} from './template-draft';
import { confirmDiscardPending, reportPendingChanges, warnOnUnload } from './unsaved-changes';
import { useLoad, useWorkspace } from './use-master-data';

// "תבנית בדיקה" in "הגדרות מסירות" (H-Checklist): the project's inspection items by room (area) and
// work category, as inspectors see them in a new handover.

type SaveState =
  | { readonly kind: 'idle' }
  | { readonly kind: 'saving' }
  | { readonly kind: 'saved' }
  | { readonly kind: 'conflict' }
  | { readonly kind: 'error'; readonly text: string };

function TemplateBody({
  projectId,
  template,
  areas,
  categories,
  components,
  justSaved,
  onSaved,
  onReload,
}: {
  readonly projectId: string;
  readonly template: InspectionTemplateResponse;
  readonly areas: readonly CatalogEntry[];
  readonly categories: readonly CatalogEntry[];
  /** Physical components (DR-189): an item may name the one it checks. */
  readonly components: readonly CatalogEntry[];
  readonly justSaved: boolean;
  readonly onSaved: (template: InspectionTemplateResponse) => void;
  readonly onReload: () => void;
}) {
  const [rows, setRows] = useState<TemplateRow[]>(() => rowsFrom(template));
  const [save, setSave] = useState<SaveState>({ kind: justSaved ? 'saved' : 'idle' });
  const [showErrors, setShowErrors] = useState(false);
  const usedAreas = areas.filter((a) => a.active || rows.some((r) => r.areaId === a.id));
  const [areaId, setAreaId] = useState<string>(usedAreas[0]?.id ?? '');
  const [newCategory, setNewCategory] = useState('');
  const [newName, setNewName] = useState('');
  // The name field of an item just added takes focus after the render that shows it.
  const focusKey = useRef<string | null>(null);
  const editable = template.permissions.edit;
  // Other projects the caller can see, to copy a template from (PROJECTS-1).
  const [workspace] = useWorkspace();
  const sources =
    workspace.state === 'ready' ? workspace.data.projects.filter((p) => p.id !== projectId) : [];
  const [source, setSource] = useState('');
  const dirty = isDirty(rows, template);
  const errors = showErrors ? validateRows(rows) : {};
  const id = useId();

  useEffect(() => {
    reportPendingChanges(dirty ? 1 : 0);
    window.addEventListener('beforeunload', warnOnUnload);
    return () => {
      window.removeEventListener('beforeunload', warnOnUnload);
      reportPendingChanges(0);
    };
  }, [dirty]);
  useEffect(() => {
    if (focusKey.current === null) return;
    document.getElementById(`template-name-${focusKey.current}`)?.focus();
    focusKey.current = null;
  });

  const nameOf = (list: readonly CatalogEntry[], entryId: string) =>
    list.find((e) => e.id === entryId)?.name ?? '';
  const change = (next: (r: TemplateRow[]) => TemplateRow[]) => {
    setRows(next);
    if (save.kind !== 'saving') setSave({ kind: 'idle' });
  };
  const update = (
    key: string,
    field: 'name' | 'active' | 'modelTypes' | 'componentId',
    value: string | boolean | null,
  ) => {
    change((list) => list.map((r) => (r.key === key ? { ...r, [field]: value } : r)));
  };
  const inArea = rows.filter((r) => r.areaId === areaId);
  const groups = [...new Set(inArea.map((r) => r.workCategoryId))].map((categoryId) => ({
    categoryId,
    rows: inArea.filter((r) => r.workCategoryId === categoryId),
  }));
  const activeCategories = categories.filter((c) => c.active);

  const submit = async () => {
    if (Object.keys(validateRows(rows)).length > 0) {
      setShowErrors(true);
      setSave({ kind: 'error', text: 'יש פריטים שדורשים תיקון. התבנית לא נשמרה.' });
      return;
    }
    setSave({ kind: 'saving' });
    try {
      const saved = await apiPut(
        `/projects/${projectId}/inspection-template`,
        toRequest(rows, template.listVersion),
        inspectionTemplateResponseSchema,
      );
      setShowErrors(false);
      setSave({ kind: 'saved' });
      onSaved(saved);
    } catch (error) {
      if (error instanceof ApiError && error.status === 409) setSave({ kind: 'conflict' });
      else if (error instanceof ApiError && error.status === 422)
        setSave({
          kind: 'error',
          text: 'השרת דחה חלק מהפריטים (למשל שם כפול, או חדר או תחום שהושבתו). בדקו ונסו שוב.',
        });
      else if (error instanceof ApiError && error.status === 403)
        setSave({ kind: 'error', text: 'אין לך הרשאה לערוך את התבנית.' });
      else if (error instanceof NetworkError)
        setSave({ kind: 'error', text: 'אין חיבור לשרת. השינויים עדיין כאן; נסו שוב.' });
      else setSave({ kind: 'error', text: 'השמירה נכשלה. השינויים עדיין כאן; נסו שוב.' });
    }
  };
  const starter = async () => {
    if (
      !(await askConfirm({
        title: 'ליצור רשימת בדיקה התחלתית?',
        message:
          'תיווצר רשימה לפי אב־הטיפוס: 8 חדרים ו-70 פריטי בדיקה. חדרים ותחומי עבודה שחסרים יתווספו. אפשר לשנות הכול אחר כך.',
        confirmLabel: 'יצירת הרשימה',
      }))
    )
      return;
    setSave({ kind: 'saving' });
    try {
      const created = await apiPost(
        `/projects/${projectId}/inspection-template/starter`,
        {},
        inspectionTemplateResponseSchema,
      );
      setSave({ kind: 'saved' });
      onSaved(created);
    } catch (error) {
      if (error instanceof ApiError && error.status === 409) setSave({ kind: 'conflict' });
      else setSave({ kind: 'error', text: 'יצירת התבנית נכשלה. נסו שוב.' });
    }
  };

  const copy = async () => {
    if (source === '') return;
    setSave({ kind: 'saving' });
    try {
      const copied = await apiPost(
        `/projects/${projectId}/inspection-template/copy`,
        { sourceProjectId: source },
        inspectionTemplateResponseSchema,
      );
      setSave({ kind: 'saved' });
      onSaved(copied);
    } catch (error) {
      setSave({ kind: 'error', text: explainProjectError(error) });
    }
  };

  return (
    <div className="template-editor">
      <p className="hint">
        פריטי הבדיקה לפי חדר ותחום עבודה. שינוי בתבנית חל על מסירות שייפתחו מעכשיו; מסירות קיימות
        שומרות את רשימת הבדיקה שהתחילו בה. גרסת תבנית: {template.revision}.
      </p>
      {rows.length === 0 && (
        <div className="notice">
          <p>התבנית ריקה. מסירה חדשה תיפתח בלי רשימת בדיקה.</p>
          {editable && (
            <button
              type="button"
              className="button button--primary"
              disabled={save.kind === 'saving'}
              onClick={() => {
                void starter();
              }}
            >
              יצירת תבנית התחלתית (מאב־הטיפוס)
            </button>
          )}
          {editable && sources.length > 0 && (
            <div className="template-copy">
              <label className="field">
                <span>או העתקה מפרויקט אחר</span>
                <select
                  value={source}
                  onChange={(e) => {
                    setSource(e.target.value);
                  }}
                >
                  <option value="">בחירת פרויקט</option>
                  {sources.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.name} ({p.code})
                    </option>
                  ))}
                </select>
              </label>
              <button
                type="button"
                className="button"
                disabled={source === '' || save.kind === 'saving'}
                onClick={() => {
                  void copy();
                }}
              >
                העתקת התבנית
              </button>
              <p className="hint">
                מועתקים הפריטים הפעילים. מסירות קיימות בשני הפרויקטים אינן משתנות.
              </p>
            </div>
          )}
        </div>
      )}
      <label className="field template-editor__area">
        <span>חדר</span>
        <select
          value={areaId}
          onChange={(e) => {
            setAreaId(e.target.value);
          }}
        >
          {usedAreas.map((area) => {
            const count = rows.filter((r) => r.areaId === area.id && r.active).length;
            return (
              <option key={area.id} value={area.id}>
                {area.name} ({count}){area.active ? '' : ' – מושבת'}
              </option>
            );
          })}
        </select>
      </label>

      {groups.length === 0 ? (
        <p className="empty">אין פריטי בדיקה בחדר זה.</p>
      ) : (
        groups.map((group) => (
          <section
            key={group.categoryId}
            className="template-group"
            aria-label={nameOf(categories, group.categoryId)}
          >
            <h3 className="template-group__title">{nameOf(categories, group.categoryId)}</h3>
            <ol className="catalog-list">
              {group.rows.map((row, index) => {
                const e = errors[row.key] ?? {};
                const name = row.name.trim() === '' ? 'פריט חדש' : row.name;
                return (
                  <li
                    key={row.key}
                    className={`catalog-row template-row${row.active ? '' : ' catalog-row--inactive'}`}
                    aria-label={name}
                  >
                    <label className="field catalog-row__name">
                      <span>פריט בדיקה</span>
                      <input
                        id={`template-name-${row.key}`}
                        value={row.name}
                        disabled={!editable}
                        aria-invalid={e.name !== undefined}
                        onChange={(event) => {
                          update(row.key, 'name', event.target.value);
                        }}
                      />
                      {e.name !== undefined && <p className="field__error">{e.name}</p>}
                    </label>
                    <label className="field template-row__component">
                      <span>רכיב</span>
                      <select
                        value={row.componentId ?? ''}
                        disabled={!editable}
                        aria-label={`רכיב: ${name}`}
                        onChange={(event) => {
                          update(
                            row.key,
                            'componentId',
                            event.target.value === '' ? null : event.target.value,
                          );
                        }}
                      >
                        <option value="">ללא רכיב</option>
                        {components
                          .filter((c) => c.active || c.id === row.componentId)
                          .map((c) => (
                            <option key={c.id} value={c.id}>
                              {c.name}
                              {!c.active && ' (מושבת)'}
                            </option>
                          ))}
                      </select>
                    </label>
                    <label className="field template-row__types">
                      <span>טיפוסי דירה (ריק: כל הדירות)</span>
                      <input
                        value={row.modelTypes}
                        disabled={!editable}
                        list={`${id}-types`}
                        aria-invalid={e.modelTypes !== undefined}
                        onChange={(event) => {
                          update(row.key, 'modelTypes', event.target.value);
                        }}
                      />
                      {e.modelTypes !== undefined && <p className="field__error">{e.modelTypes}</p>}
                    </label>
                    <label className="catalog-row__active" title="סטטוס">
                      <input
                        type="checkbox"
                        checked={row.active}
                        disabled={!editable}
                        onChange={(event) => {
                          update(row.key, 'active', event.target.checked);
                        }}
                      />
                      <span>פעיל</span>
                    </label>
                    {row.id === null && <span className="chip chip--new">חדש</span>}
                    {editable && (
                      <span className="catalog-row__move">
                        <button
                          type="button"
                          className="button button--small"
                          aria-label={`העברה למעלה: ${name}`}
                          disabled={index === 0}
                          onClick={() => {
                            change((list) => moveRow(list, row.key, -1));
                          }}
                        >
                          ▲
                        </button>
                        <button
                          type="button"
                          className="button button--small"
                          aria-label={`העברה למטה: ${name}`}
                          disabled={index === group.rows.length - 1}
                          onClick={() => {
                            change((list) => moveRow(list, row.key, 1));
                          }}
                        >
                          ▼
                        </button>
                      </span>
                    )}
                  </li>
                );
              })}
            </ol>
          </section>
        ))
      )}
      <datalist id={`${id}-types`}>
        {template.modelTypes.map((t) => (
          <option key={t} value={t} />
        ))}
      </datalist>

      {editable && areaId !== '' && (
        <form
          className="template-add"
          onSubmit={(e) => {
            e.preventDefault();
            const categoryId = newCategory === '' ? activeCategories[0]?.id : newCategory;
            if (categoryId === undefined || newName.trim() === '') return;
            const added = addRow(rows, areaId, categoryId, newName.trim());
            change(() => added.rows);
            setNewName('');
            focusKey.current = added.key;
          }}
        >
          <label className="field">
            <span>תחום עבודה</span>
            <select
              value={newCategory}
              onChange={(e) => {
                setNewCategory(e.target.value);
              }}
            >
              {activeCategories.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
          </label>
          <label className="field">
            <span>פריט בדיקה חדש</span>
            <input
              value={newName}
              onChange={(e) => {
                setNewName(e.target.value);
              }}
            />
          </label>
          <button type="submit" className="button" disabled={newName.trim() === ''}>
            + הוספת פריט
          </button>
        </form>
      )}

      {editable && (
        <div className="save-bar" role="region" aria-label="שמירת התבנית">
          {save.kind === 'conflict' ? (
            <div className="notice notice--error save-bar__notice" role="alert">
              <p>התבנית עודכנה במקום אחר מאז שנפתחה. השינויים שלך לא נשמרו.</p>
              <button
                type="button"
                className="button button--small"
                onClick={() => {
                  void confirmDiscardPending().then((ok) => {
                    if (ok) onReload();
                  });
                }}
              >
                טעינת הגרסה העדכנית
              </button>
            </div>
          ) : save.kind === 'error' ? (
            <p className="notice notice--error save-bar__notice" role="alert">
              {save.text}
            </p>
          ) : null}
          <p className={`save-bar__status${dirty ? ' save-bar__status--dirty' : ''}`} role="status">
            {save.kind === 'saving'
              ? 'שומר…'
              : dirty
                ? 'יש שינויים שלא נשמרו'
                : save.kind === 'saved'
                  ? 'התבנית נשמרה'
                  : 'כל השינויים שמורים'}
          </p>
          <div className="save-bar__actions">
            <button
              type="button"
              className="button"
              disabled={!dirty || save.kind === 'saving'}
              onClick={() => {
                void askConfirm({
                  title: 'לבטל את השינויים שלא נשמרו?',
                  confirmLabel: 'ביטול השינויים',
                  danger: true,
                }).then((yes) => {
                  if (!yes) return;
                  setRows(rowsFrom(template));
                  setShowErrors(false);
                  setSave({ kind: 'idle' });
                });
              }}
            >
              ביטול שינויים
            </button>
            <button
              type="button"
              className="button button--primary"
              disabled={!dirty || save.kind === 'saving'}
              onClick={() => {
                void submit();
              }}
            >
              שמירת התבנית
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export function InspectionTemplateEditor({ projectId }: { readonly projectId: string }) {
  const [version, setVersion] = useState(0);
  const [saved, setSaved] = useState<InspectionTemplateResponse | null>(null);
  const [data, reload] = useLoad(`inspection-template:${projectId}:${String(version)}`, () =>
    apiGet(`/projects/${projectId}/inspection-template`, inspectionTemplateResponseSchema),
  );
  // Rooms, work categories and components (reloaded with the template: the starter may add some).
  const [entries] = useLoad(
    `inspection-template-entries:${projectId}:${String(version)}:${saved?.revision ?? ''}`,
    async () => ({
      areas: (await apiGet(`/projects/${projectId}/catalogs/area`, catalogListResponseSchema))
        .items,
      categories: (
        await apiGet(`/projects/${projectId}/catalogs/work_category`, catalogListResponseSchema)
      ).items,
      components: (
        await apiGet(`/projects/${projectId}/catalogs/component`, catalogListResponseSchema)
      ).items,
    }),
  );
  const template = saved ?? (data.state === 'ready' ? data.data : null);
  if (template === null || entries.state !== 'ready') {
    if (data.state === 'forbidden' || entries.state === 'forbidden')
      return <p className="notice notice--error">אין לך הרשאה לצפות בתבנית הבדיקה.</p>;
    if (data.state === 'error' || entries.state === 'error')
      return (
        <div className="notice notice--error" role="alert">
          <p>טעינת תבנית הבדיקה נכשלה.</p>
          <button type="button" className="button" onClick={reload}>
            נסו שוב
          </button>
        </div>
      );
    return (
      <div className="skeleton" role="status" aria-label="טוען נתונים">
        <div className="skeleton__row" />
        <div className="skeleton__row" />
      </div>
    );
  }
  return (
    <TemplateBody
      key={template.listVersion}
      projectId={projectId}
      template={template}
      areas={entries.data.areas}
      categories={entries.data.categories}
      components={entries.data.components}
      justSaved={saved !== null}
      onSaved={setSaved}
      onReload={() => {
        setSaved(null);
        setVersion((v) => v + 1);
      }}
    />
  );
}
